import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Shield, Plus, Pencil, Trash2, Search, DollarSign, Users, FileText } from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';

export default function InsuranceProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [billingDialog, setBillingDialog] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', coverage_percent: 80, contract_terms: '', notes: '' });

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/insurance-providers', { params: { search } });
      setProviders(res.data.providers || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(fetchProviders, 300); return () => clearTimeout(t); }, [fetchProviders]);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/insurance-providers/${editing}`, form);
        toast.success('Provider updated');
      } else {
        await api.post('/insurance-providers', form);
        toast.success('Provider created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', contact_person: '', email: '', phone: '', address: '', coverage_percent: 80, contract_terms: '', notes: '' });
      fetchProviders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving provider'); }
  };

  const handleEdit = (p) => {
    setEditing(p.provider_id);
    setForm({ name: p.name, contact_person: p.contact_person || '', email: p.email || '', phone: p.phone || '', address: p.address || '', coverage_percent: p.coverage_percent || 80, contract_terms: p.contract_terms || '', notes: p.notes || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this insurance provider?')) return;
    try { await api.delete(`/insurance-providers/${id}`); toast.success('Deleted'); fetchProviders(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const handleViewBilling = async (provider) => {
    setBillingDialog(provider);
    try {
      const res = await api.get(`/insurance-providers/${provider.provider_id}/billing`);
      setBillingData(res.data);
    } catch (e) { toast.error('Failed to load billing'); }
  };

  return (
    <div className="space-y-6" data-testid="insurance-providers-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Shield className="h-6 w-6 text-primary" /> Insurance Partners</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage insurance providers and view billing</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: '', contact_person: '', email: '', phone: '', address: '', coverage_percent: 80, contract_terms: '', notes: '' }); setDialogOpen(true); }} data-testid="add-insurance-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Provider
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search providers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-insurance" />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Coverage %</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {providers.map(p => (
                <TableRow key={p.provider_id} data-testid={`insurance-row-${p.provider_id}`}>
                  <TableCell>
                    <div><span className="font-medium">{p.name}</span></div>
                    {p.email && <div className="text-xs text-muted-foreground">{p.email}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{p.contact_person || '—'}</div>
                    {p.phone && <div className="text-xs text-muted-foreground">{p.phone}</div>}
                  </TableCell>
                  <TableCell><Badge variant="secondary">{p.coverage_percent}%</Badge></TableCell>
                  <TableCell><Badge variant={p.status === 'active' ? 'default' : 'secondary'}>{p.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewBilling(p)} title="View Billing" data-testid={`billing-${p.provider_id}`}><DollarSign className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(p)} data-testid={`edit-${p.provider_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(p.provider_id)} data-testid={`delete-${p.provider_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && providers.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No insurance providers found. Add one to get started.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Insurance Provider</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Provider Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="ins-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} /></div>
              <div className="space-y-1"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div className="space-y-1"><Label>Default Coverage %</Label><Input type="number" min={0} max={100} value={form.coverage_percent} onChange={(e) => setForm({...form, coverage_percent: parseFloat(e.target.value) || 0})} data-testid="ins-coverage-input" /></div>
            </div>
            <div className="space-y-1"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div className="space-y-1"><Label>Contract Terms</Label><Textarea value={form.contract_terms} onChange={(e) => setForm({...form, contract_terms: e.target.value})} className="h-16" /></div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="h-16" /></div>
            <Button className="w-full" onClick={handleSave} disabled={!form.name} data-testid="save-insurance-btn">{editing ? 'Update' : 'Create'} Provider</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Billing Dialog */}
      <Dialog open={!!billingDialog} onOpenChange={() => { setBillingDialog(null); setBillingData(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> Billing — {billingDialog?.name}</DialogTitle></DialogHeader>
          {billingData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total Sales</p><p className="text-xl font-bold">{billingData.billing_summary.total_sales}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total Billed</p><p className="text-xl font-bold text-primary">{fmtCurrency(billingData.billing_summary.total_billed)}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Claims Amount</p><p className="text-xl font-bold text-blue-500">{fmtCurrency(billingData.billing_summary.total_claims)}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Clients Served</p><p className="text-xl font-bold">{billingData.billing_summary.clients_served}</p></CardContent></Card>
              </div>
              {billingData.sales.length > 0 && (
                <Table>
                  <TableHeader><TableRow><TableHead>Sale ID</TableHead><TableHead>Customer</TableHead><TableHead>Policy #</TableHead><TableHead>Total</TableHead><TableHead>Claim</TableHead><TableHead>Copay</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {billingData.sales.slice(0, 20).map(s => (
                      <TableRow key={s.sale_id}>
                        <TableCell className="text-xs font-mono">{s.sale_id?.slice(-8)}</TableCell>
                        <TableCell className="text-sm">{s.customer_name || '—'}</TableCell>
                        <TableCell className="text-xs">{s.policy_number || '—'}</TableCell>
                        <TableCell className="text-sm font-medium">{fmtCurrency(s.total)}</TableCell>
                        <TableCell className="text-sm text-blue-500">{fmtCurrency((s.claim_amount || 0))}</TableCell>
                        <TableCell className="text-sm">{fmtCurrency((s.copay_amount || 0))}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {billingData.sales.length === 0 && <p className="text-center text-muted-foreground py-6">No sales recorded for this provider yet.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
